import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarIcon,
  UserGroupIcon,
  ClockIcon,
  MapPinIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { toastError, toastSuccess } from "../utils/toast";

const monthNames = [
  "Gennaio",
  "Febbraio",
  "Marzo",
  "Aprile",
  "Maggio",
  "Giugno",
  "Luglio",
  "Agosto",
  "Settembre",
  "Ottobre",
  "Novembre",
  "Dicembre",
];

const getMonthRange = (monthIndex, year) => {
  const startDate = new Date(Date.UTC(year, monthIndex, 1));
  const endDate = new Date(Date.UTC(year, monthIndex + 1, 0));
  return {
    startStr: startDate.toISOString().slice(0, 10),
    endStr: endDate.toISOString().slice(0, 10),
  };
};

const computeDefaultSelection = () => {
  const now = new Date();
  let monthIndex = now.getMonth();
  let year = now.getFullYear();

  if (now.getDate() > 10) {
    monthIndex += 1;
    if (monthIndex === 12) {
      monthIndex = 0;
      year += 1;
    }
  }

  return { monthIndex, year };
};

const DisponibilitaConduttori = () => {
  const { user, activeCongregazione } = useAuth();
  const [occurrences, setOccurrences] = useState([]);
  const [availability, setAvailability] = useState({});
  const [dirtyAvailability, setDirtyAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const defaultSelection = useMemo(() => computeDefaultSelection(), []);
  const [selectedMonth, setSelectedMonth] = useState(defaultSelection.monthIndex);
  const [selectedYear, setSelectedYear] = useState(defaultSelection.year);
  const initialAvailabilityRef = useRef({});

  const fallbackCongregazione =
    activeCongregazione ||
    (user?.congregazione_id
      ? {
          id: user.congregazione_id,
          servizio_speciale: user.congregazione_servizio_speciale,
        }
      : null);

  const congregazioneId = fallbackCongregazione?.id;
  const isServizioSpeciale = Boolean(fallbackCongregazione?.servizio_speciale);
  const isServiceLeader = user?.service_leader && user?.sesso === "M";
  const isSuperAdmin =
    user?.ruolo === "super_admin" || user?.ruolo_base === "super_admin";
  const isSupervisionMode = isSuperAdmin;
  const canAccessPage =
    Boolean(congregazioneId) &&
    !isServizioSpeciale &&
    (isServiceLeader || isSupervisionMode);

  useEffect(() => {
    if (!canAccessPage) {
      setOccurrences([]);
      setAvailability({});
      setDirtyAvailability({});
      initialAvailabilityRef.current = {};
      setLoading(false);
      return;
    }

    const loadMeetings = async () => {
      try {
        setLoading(true);
        const { startStr, endStr } = getMonthRange(
          selectedMonth,
          selectedYear
        );
        const response = await api.get(
          isSupervisionMode
            ? "/adunanze-servizio/disponibilita/overview"
            : `/adunanze-servizio/volontario/${user.id}/disponibilita`,
          {
            params: {
              congregazione_id: congregazioneId,
              data_inizio: startStr,
              data_fine: endStr,
            },
          }
        );
        const data = Array.isArray(response.data?.occorrenze)
          ? response.data.occorrenze
          : [];
        const map = {};
        data.forEach((meeting) => {
          map[meeting.occurrence_id] =
            meeting.disponibilita_stato === "disponibile";
        });
        setOccurrences(data);
        setAvailability(map);
        initialAvailabilityRef.current = { ...map };
        setDirtyAvailability({});
      } catch (error) {
        console.error("Errore caricamento disponibilità conduttori:", error);
        toastError(
          error.response?.data?.message ||
            "Errore durante il caricamento delle adunanze"
        );
        setOccurrences([]);
        setAvailability({});
        setDirtyAvailability({});
        initialAvailabilityRef.current = {};
      } finally {
        setLoading(false);
      }
    };

    loadMeetings();
  }, [
    canAccessPage,
    isSupervisionMode,
    user?.id,
    congregazioneId,
    selectedMonth,
    selectedYear,
  ]);

  const handleChange = (occurrenceId, disponibile) => {
    if (isSupervisionMode) return;

    setAvailability((prev) => ({ ...prev, [occurrenceId]: disponibile }));
    setDirtyAvailability((prev) => {
      const updated = { ...prev };
      const initialValue =
        initialAvailabilityRef.current[occurrenceId] || false;
      if (initialValue === disponibile) {
        delete updated[occurrenceId];
        return updated;
      }
      updated[occurrenceId] = disponibile;
      return updated;
    });
  };

  const occurrenceById = useMemo(() => {
    const map = {};
    occurrences.forEach((occ) => {
      map[occ.occurrence_id] = occ;
    });
    return map;
  }, [occurrences]);

  const getTotalDisponibili = (occurrence) => {
    const occurrenceId = occurrence.occurrence_id;
    const initialValue = Boolean(initialAvailabilityRef.current[occurrenceId]);
    const currentValue = Boolean(availability[occurrenceId]);
    const delta = (currentValue ? 1 : 0) - (initialValue ? 1 : 0);

    return Math.max((Number(occurrence.totale_disponibili) || 0) + delta, 0);
  };

  const renderAvailabilityBadge = (occurrence) => {
    const totalAvailable = getTotalDisponibili(occurrence);
    const label = `Conduttori disponibili: ${totalAvailable}`;

    return (
      <span
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary-200 bg-primary-50 text-xs font-semibold text-primary-700"
        title={label}
        aria-label={label}
      >
        {totalAvailable}
      </span>
    );
  };

  const handleSave = async () => {
    if (isSupervisionMode) return;

    const pendingEntries = Object.entries(dirtyAvailability)
      .map(([occurrenceId, disponibile]) => {
        const occurrence = occurrenceById[occurrenceId];
        if (!occurrence) {
          return null;
        }
        return {
          occurrenceId,
          payload: {
            adunanza_id: occurrence.adunanza_id,
            data: occurrence.data,
            stato: disponibile ? "disponibile" : "non_disponibile",
          },
        };
      })
      .filter(Boolean);

    const entries = pendingEntries.map((entry) => entry.payload);

    if (entries.length === 0) {
      toastSuccess("Nessuna modifica da salvare.");
      return;
    }

    try {
      setSaving(true);
      await api.post("/adunanze-servizio/disponibilita", {
        volontario_id: user.id,
        disponibilita: entries,
      });
      toastSuccess("Disponibilità aggiornate con successo.");
      const initialAvailability = { ...initialAvailabilityRef.current };
      setOccurrences((previous) => {
        const changesByOccurrenceId = new Map(
          pendingEntries.map(({ occurrenceId, payload }) => [
            occurrenceId,
            payload.stato === "disponibile",
          ])
        );

        return previous.map((occurrence) => {
          if (!changesByOccurrenceId.has(occurrence.occurrence_id)) {
            return occurrence;
          }

          const wasAvailable = Boolean(
            initialAvailability[occurrence.occurrence_id]
          );
          const isAvailable = changesByOccurrenceId.get(
            occurrence.occurrence_id
          );
          const delta = (isAvailable ? 1 : 0) - (wasAvailable ? 1 : 0);

          return {
            ...occurrence,
            totale_disponibili: Math.max(
              (Number(occurrence.totale_disponibili) || 0) + delta,
              0
            ),
          };
        });
      });
      setDirtyAvailability({});
      const newInitial = initialAvailability;
      pendingEntries.forEach(({ occurrenceId, payload }) => {
        newInitial[occurrenceId] = payload.stato === "disponibile";
      });
      initialAvailabilityRef.current = newInitial;
    } catch (error) {
      console.error("Errore salvataggio disponibilità conduttori:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante il salvataggio delle disponibilità"
      );
    } finally {
      setSaving(false);
    }
  };

  const monthLabel = useMemo(() => {
    const monthName = monthNames[selectedMonth] || "";
    return `${monthName} ${selectedYear}`;
  }, [selectedMonth, selectedYear]);

  const goToPreviousMonth = () => {
    setSelectedMonth((prev) => {
      if (prev === 0) {
        setSelectedYear((year) => year - 1);
        return 11;
      }
      return prev - 1;
    });
  };

  const goToNextMonth = () => {
    setSelectedMonth((prev) => {
      if (prev === 11) {
        setSelectedYear((year) => year + 1);
        return 0;
      }
      return prev + 1;
    });
  };

  if (!congregazioneId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <p className="text-gray-600">
            Seleziona prima una congregazione per consultare le disponibilità.
          </p>
        </div>
      </div>
    );
  }

  if (isServizioSpeciale) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white shadow rounded-lg p-6 text-center space-y-2">
          <p className="text-sm font-semibold text-gray-900">
            Modalità non disponibile
          </p>
          <p className="text-gray-600 text-sm">
            Le congregazioni di servizio speciale utilizzano la pagina
            “Disponibilità” tradizionale. Torna alla sezione principale per
            gestire le opzioni disponibili.
          </p>
        </div>
      </div>
    );
  }

  if (!isServiceLeader && !isSupervisionMode) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white shadow rounded-lg p-6 text-center space-y-2">
          <p className="text-sm font-semibold text-gray-900">
            Accesso riservato ai conduttori
          </p>
          <p className="text-gray-600 text-sm">
            Questa pagina è riservata ai proclamatori uomini autorizzati come
            conduttori delle adunanze per il servizio.
          </p>
        </div>
      </div>
    );
  }

  const hasDirtyChanges = Object.keys(dirtyAvailability).length > 0;

  const renderMobileCard = (occurrence) => {
    const dateObj = new Date(`${occurrence.data}T00:00:00Z`);
    const formattedDate = dateObj.toLocaleDateString("it-IT", {
      weekday: "long",
      day: "2-digit",
      month: "long",
    });
    const oraLabel = occurrence.ora_inizio
      ? occurrence.ora_inizio.substring(0, 5)
      : "—";

    return (
      <div
        key={occurrence.occurrence_id}
        className="relative bg-white shadow rounded-lg p-4 pl-6 space-y-3 transition hover:bg-gray-50 hover:shadow-sm"
      >
        <span
          className="accent-bar top-4 bottom-4"
          aria-hidden="true"
        />
        <div>
          <p className="font-semibold text-gray-900 capitalize">
            {formattedDate}
          </p>
          {occurrence.titolo && (
            <p className="text-sm text-gray-500">{occurrence.titolo}</p>
          )}
        </div>
        <div className="text-sm text-gray-700 space-y-2">
          <div className="flex items-center gap-2">
            <MapPinIcon className="h-4 w-4 text-gray-400" />
            <span>{occurrence.luogo}</span>
          </div>
          {occurrence.indirizzo && (
            <p className="text-xs text-gray-500 ml-6">{occurrence.indirizzo}</p>
          )}
          <div className="flex items-center gap-2">
            <ClockIcon className="h-4 w-4 text-gray-400" />
            <span>{oraLabel}</span>
          </div>
          <div className="text-xs text-gray-500">
            {occurrence.leader
              ? `Conduttore assegnato: ${occurrence.leader.nome} ${occurrence.leader.cognome}`
              : "Conduttore non assegnato"}
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          {isSupervisionMode ? (
            <span className="text-sm font-medium text-gray-700">
              Disponibili
            </span>
          ) : (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-5 w-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                checked={Boolean(availability[occurrence.occurrence_id])}
                onChange={(e) =>
                  handleChange(occurrence.occurrence_id, e.target.checked)
                }
              />
              Disponibile
            </label>
          )}
          {renderAvailabilityBadge(occurrence)}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
      <div className="bg-white shadow rounded-lg p-4 md:p-6 space-y-3">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <UserGroupIcon className="h-7 w-7 text-primary-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isSupervisionMode
                ? "Disponibilità conduttori"
                : "Disponibilità A.S."}
            </h1>
            <p className="text-gray-600 text-sm">
              {isSupervisionMode
                ? `Modalità supervisione: riepilogo aggregato per ${
                    fallbackCongregazione?.nome || "la congregazione selezionata"
                  }. I nominativi non sono esposti.`
                : "Metti il check sulle adunanze che puoi presiedere."}
            </p>
            <div className="accent-line mt-3" />
          </div>
        </div>
        {!isSupervisionMode && (
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving || !hasDirtyChanges || loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? "Salvataggio..." : "Salva disponibilità"}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 md:px-6 py-4 border-b border-gray-200 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-primary-600" />
              Disponibilità mensile
            </h2>
            <p className="text-sm text-gray-500">
              {isSupervisionMode
                ? "Consulta il totale dei conduttori disponibili per ciascuna adunanza programmata."
                : "Seleziona un mese per indicare le tue disponibilità sulle singole adunanze programmate."}
            </p>
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-700 flex-wrap">
            <button
              onClick={goToPreviousMonth}
              className="p-2 rounded-full border border-gray-200 hover:bg-gray-100 text-gray-600"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <span className="text-sm font-medium text-gray-900">
              {monthLabel}
            </span>
            <button
              onClick={goToNextMonth}
              className="p-2 rounded-full border border-gray-200 hover:bg-gray-100 text-gray-600"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="accent-line mx-4 md:mx-6" />

        {loading ? (
          <div className="p-4 md:p-6 text-center text-gray-600">
            {isSupervisionMode
              ? "Caricamento riepilogo conduttori..."
              : "Caricamento in corso..."}
          </div>
        ) : occurrences.length === 0 ? (
          <div className="p-4 md:p-6 text-center text-gray-600">
            Nessuna adunanza programmata nel periodo selezionato.
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Luogo
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Orario
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conduttore assegnato
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Disponibilità
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {occurrences.map((occurrence) => {
                  const dateObj = new Date(`${occurrence.data}T00:00:00Z`);
                  const formattedDate = dateObj.toLocaleDateString("it-IT", {
                    weekday: "long",
                    day: "2-digit",
                    month: "long",
                  });
                  const oraLabel = occurrence.ora_inizio
                    ? occurrence.ora_inizio.substring(0, 5)
                    : "—";

                  return (
                    <tr key={occurrence.occurrence_id} className="hover:bg-gray-50">
                      <td className="px-4 md:px-6 py-4 capitalize relative pl-6">
                        <span
                          className="accent-bar top-3 bottom-3"
                          aria-hidden="true"
                        />
                        <p className="font-medium text-gray-900">
                          {formattedDate}
                        </p>
                        {occurrence.titolo && (
                          <p className="text-xs text-gray-500">
                            {occurrence.titolo}
                          </p>
                        )}
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div className="flex items-center gap-2 text-gray-900">
                          <MapPinIcon className="h-4 w-4 text-gray-400" />
                          <span>{occurrence.luogo}</span>
                        </div>
                        {occurrence.indirizzo && (
                          <p className="text-xs text-gray-500 ml-6">
                            {occurrence.indirizzo}
                          </p>
                        )}
                      </td>
                      <td className="px-4 md:px-6 py-4 text-gray-900">
                        <div className="flex items-center gap-1">
                          <ClockIcon className="h-4 w-4 text-gray-400" />
                          <span>{oraLabel}</span>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        {occurrence.leader ? (
                          <div className="text-sm text-gray-900">
                            {occurrence.leader.nome} {occurrence.leader.cognome}
                          </div>
                        ) : (
                          <span className="text-gray-500 text-sm">
                            Non assegnato
                          </span>
                        )}
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div className="inline-flex items-center gap-3">
                          {isSupervisionMode ? (
                            <span className="text-sm font-medium text-gray-700">
                              Disponibili
                            </span>
                          ) : (
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                className="h-5 w-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                checked={Boolean(
                                  availability[occurrence.occurrence_id]
                                )}
                                onChange={(e) =>
                                  handleChange(
                                    occurrence.occurrence_id,
                                    e.target.checked
                                  )
                                }
                              />
                              Disponibile
                            </label>
                          )}
                          {renderAvailabilityBadge(occurrence)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <div className="md:hidden space-y-4">
              {occurrences.map((occurrence) => renderMobileCard(occurrence))}
            </div>
            {!isSupervisionMode && (
              <div className="md:hidden py-4 border-t border-gray-100 flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving || !hasDirtyChanges || loading}
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? "Salvataggio..." : "Salva disponibilità"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DisponibilitaConduttori;
